// Safe-by-default HTML templating for the innerHTML-based dashboard renderer.
// Every value interpolated into html`` is escaped unless it is itself SafeHtml
// (another html`` result, or a trusted constant wrapped in raw()), so dashboard
// content — which external users can edit — can't inject markup or script.

export class SafeHtml {
  constructor(readonly value: string) { }
  toString(): string { return this.value; }
}

const ESCAPES: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

function escape(s: string): string {
  return s.replace(/[&<>"']/g, c => ESCAPES[c]);
}

function render(v: unknown): string {
  if (v instanceof SafeHtml) return v.value;
  if (Array.isArray(v)) return v.map(render).join('');
  if (v === null || v === undefined || v === false) return '';
  return escape(String(v));
}

export function html(strings: TemplateStringsArray, ...values: unknown[]): SafeHtml {
  let out = strings[0];
  values.forEach((v, i) => { out += render(v) + strings[i + 1]; });
  return new SafeHtml(out);
}

// Trusted, constant markup only — never dashboard content.
export function raw(s: string): SafeHtml {
  return new SafeHtml(s);
}

// An argument for an inline event handler: onclick="fn(${jsArg(id)})".
// HTML-escaping alone isn't enough there — the browser decodes entities before
// running the handler — so the value is also made a JS string literal.
export function jsArg(v: unknown): SafeHtml {
  return new SafeHtml(escape(JSON.stringify(String(v ?? ''))));
}

// The only way dashboard markup reaches the DOM.
export function setHtml(el: Element, content: SafeHtml): void {
  el.innerHTML = content.value;
}
